import React, { useState, useEffect, useMemo } from 'react';
import { ConfigProvider } from 'antd';
import { CustomLoader } from '@components/UI/CustomLoader/Index.jsx';
import { StyledTable } from '@styles/datatables/datatable-antd.js';
import ptBR from 'antd/es/locale/pt_BR';
import TextBlock from '@components/UI/TextBlock/Index..jsx';
import { GetDataFormulariosForReports } from '@services/Formulario/Index';
import ExportToExcelButton from '@components/UI/ExportToExcel/Index';
import EllipsisText from '@components/UI/EllipsisText/Index';
import { formatRespostaParaDisplay, IGNORED_TYPES } from '@utils/formulario/FormularioRenderHelpers';
import { formatDateTimeToBR } from '@utils/FormatDate';
import { tipoAssociacaoOptions } from '@enums/RelatorioFormularios/Index';

const GridReportFormularios = ({ state, setData, filters }) => {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  const apiFilters = useMemo(() => ({
    tipoAssociacao: filters.tipoAssociacao || null,
    numeroSolicitacao: filters.numeroSolicitacao || null,
    tituloFormulario: filters.tituloFormulario || null,
    dataEnvioRespostaInicial: filters.dataEnvioRespostaInicial || null,
    dataEnvioRespostaFinal: filters.dataEnvioRespostaFinal || null,
    nomeUnidadeGerencia: filters.nomeUnidadeGerencia || null,
    nomeProcesso: filters.nomeProcesso || null,
  }), [filters]);

  // Buscar dados apenas se ao menos um filtro for preenchido
  const fetchData = async (currentPage, currentRowsPerPage, apiFilters = {}) => {
    setLoading(true);
    try {
      const response = await GetDataFormulariosForReports(currentPage, currentRowsPerPage, apiFilters);
      setData(response.items, response.totalItems);
    } catch (error) {
      console.error('Erro ao buscar dados dos formulários:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }
    fetchData(page, rowsPerPage, apiFilters);
    // eslint-disable-next-line
  }, [page, rowsPerPage, apiFilters]);

  // Descobre as perguntas dinamicamente
  const allQuestions = useMemo(() => {
    if (!state.data || state.data.length === 0) return [];
    return [
      ...new Set(
        state.data.flatMap(item => item.perguntas?.map(q => q.title) || [])
      )
    ];
  }, [state.data]);

  // Monta o dataSource já formatado para exibição e exportação
  const dataSource = useMemo(() => {
    return (state.data || []).map((item, idx) => {
      const row = {
        key: idx,
        numeroSolicitacao: item.numeroSolicitacao,
        nomeUnidadeGerencia: item.nomeUnidadeGerencia,
        tituloFormulario: item.tituloFormulario,
        nomeProcesso: item.nomeProcesso,
        dataEnvioResposta: formatDateTimeToBR(item.dataEnvioResposta),
        tipoAssociacao: tipoAssociacaoOptions.find(option => option.value === item.tipoAssociacao)?.label || item.tipoAssociacao
      };

      // Mapeia perguntas desse item (por título)
      const perguntasByTitle = {};
      (item.perguntas || []).forEach(q => {
        if (!IGNORED_TYPES.includes(q.type)) {
          perguntasByTitle[q.title] = q;
        }
      });

      // Garante TODAS as perguntas como colunas, preenchendo "-" se não existe
      allQuestions.forEach(title => {
        const pergunta = perguntasByTitle[title];
        row[title] = formatRespostaParaDisplay(
          pergunta ? pergunta.resposta : undefined,
          pergunta // se não existe, undefined
        );
      });

      return row;
    });
  }, [state.data, allQuestions]);

  // Colunas dinâmicas, sem duplicar lógica de formatação!
  const columns = useMemo(() => [
    {
      title: 'N° Solicitação',
      dataIndex: 'numeroSolicitacao',
      key: 'numeroSolicitacao',
      sorter: (a, b) => a.numeroSolicitacao.localeCompare(b.numeroSolicitacao),
      width: 200,
    },
    {
      title: 'Unidade',
      dataIndex: 'nomeUnidadeGerencia',
      key: 'nomeUnidadeGerencia',
      sorter: (a, b) => a.nomeUnidadeGerencia.localeCompare(b.nomeUnidadeGerencia),
      width: 200,
    },
    {
      title: 'Processo',
      dataIndex: 'nomeProcesso',
      key: 'nomeProcesso',
      sorter: (a, b) => a.nomeProcesso.localeCompare(b.nomeProcesso),
      width: 200,
    },
    {
      title: 'Modelo do Formulário',
      dataIndex: 'tituloFormulario',
      key: 'tituloFormulario',
      width: 250,
      sorter: (a, b) => a.tituloFormulario.localeCompare(b.tituloFormulario),
      render: (text) => <TextBlock>{text}</TextBlock>,
    },
    {
      title: 'Data Envio Resposta',
      dataIndex: 'dataEnvioResposta',
      key: 'dataEnvioResposta',
      width: 150,
      sorter: (a, b) => a.dataEnvioResposta.localeCompare(b.dataEnvioResposta),
      render: (text) => (text),
    },
    {
      title: 'Tipo de Associação',
      dataIndex: 'tipoAssociacao',
      key: 'tipoAssociacao',
      sorter: (a, b) => a.tipoAssociacao.localeCompare(b.tipoAssociacao),
      width: 200,
    },
    ...allQuestions.map(title => ({
      title: <EllipsisText maxHeight={40} lines={2}>{title}</EllipsisText>,
      dataIndex: title,
      key: title,
      render: value => (
        <EllipsisText maxHeight={40} lines={2}>{value}</EllipsisText>
      ),
      width: 220,
    })),
  ], [allQuestions]);

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  return (
    <div className="container-fluid">

      <div className="mt-3 mb-3 d-flex justify-content-between align-items-center">
        <h1>Relatórios de Formulários</h1>

        {dataSource && dataSource.length > 0 && (
          <ExportToExcelButton
            data={dataSource}
            fileName={'relatorio_formularios.xlsx'}
            sheetName={'Relatório'}
          />
        )}

      </div>

      <ConfigProvider locale={ptBR}>
        <StyledTable
          rowKey="key"
          columns={columns}
          dataSource={dataSource}
          loading={{
            spinning: loading,
            indicator: <CustomLoader />,
          }}
          pagination={{
            current: page,
            pageSize: rowsPerPage,
            total: state.totalRows,
            onChange: handlePageChange,
            showSizeChanger: true,
            onShowSizeChange: handlePageChange,
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
          }}
          scroll={{ x: "max-content", y: "20rem" }}
          locale={{
            emptyText: <h2 className="m-3">Nenhum registro disponível para exibição.</h2>,
          }}
        />
      </ConfigProvider>
    </div>
  );
};

export default GridReportFormularios;
